/**
 * EventManager - Base class for event management functionality
 * Provides consistent event handling patterns across TitusJS components
 */
export default class EventManager {
	/** @type {Object<string, Function[]>} Event listeners registry - arrays of callbacks per event */
	#eventListeners = {};

	/**
	 * Add an event listener
	 * @param {string} eventName - Name of the event
	 * @param {Function} callback - Callback function
	 */
	addEventListener(eventName, callback) {
		if (typeof callback !== "function") {
			throw new Error(
				`EventManager: Callback must be a function, got ${typeof callback}`,
			);
		}

		if (!this.#eventListeners[eventName]) {
			this.#eventListeners[eventName] = [];
		}

		// Add callback to array (allows multiple listeners per event)
		this.#eventListeners[eventName].push(callback);
	}

	/**
	 * Remove event listener(s)
	 * @param {string} eventName - Name of the event
	 * @param {Function} [callback] - Specific callback to remove. If not provided, removes all listeners for this event
	 */
	removeEventListener(eventName, callback = null) {
		if (!this.#eventListeners[eventName]) {
			return;
		}

		if (callback === null) {
			delete this.#eventListeners[eventName];
		} else {
			// Remove specific callback
			const listeners = this.#eventListeners[eventName];
			const index = listeners.indexOf(callback);
			if (index > -1) {
				listeners.splice(index, 1);
			}

			// Clean up empty array
			if (listeners.length === 0) {
				delete this.#eventListeners[eventName];
			}
		}
	}

	/**
	 * Trigger an event and call all registered listeners
	 * @param {string} eventName - Name of the event
	 * @param {*} data - Data to pass to all listeners
	 * @protected
	 */
	triggerEvent(eventName, data = null) {
		const listeners = this.#eventListeners[eventName];
		if (listeners && listeners.length > 0) {
			// Call all registered listeners for this event
			listeners.forEach((listener, index) => {
				try {
					listener(data);
				} catch (error) {
					console.error(
						`TitusJS: Error in ${eventName} listener #${index + 1}:`,
						error,
					);
				}
			});
		}
	}

	/**
	 * Check if an event has any listeners
	 * @param {string} eventName - Name of the event to check
	 * @returns {boolean} True if event has listeners
	 */
	hasListeners(eventName) {
		return !!(
			this.#eventListeners[eventName] &&
			this.#eventListeners[eventName].length > 0
		);
	}

	/**
	 * Get the number of listeners for an event
	 * @param {string} eventName - Name of the event
	 * @returns {number} Number of registered listeners
	 */
	getListenerCount(eventName) {
		return this.#eventListeners[eventName]
			? this.#eventListeners[eventName].length
			: 0;
	}

	/**
	 * Remove all event listeners
	 * @protected
	 */
	clearAllListeners() {
		this.#eventListeners = {};
	}
}
